import { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, FileWarning, Gavel, Loader2, RefreshCw, X } from 'lucide-react';
import {
  BUREAUS, accountKey, acctName, errText, ftcAccounts, makeRoundLetter, redateLetter, regenerateFtc, todayIso, uniqueAccounts,
} from '../lib/quickRedo';

const API = process.env.REACT_APP_BACKEND_URL;
const ROUND = { second_round: ['regular'], third_round: ['second_round', 'regular'] };
const TITLES = { ftc: 'New FTC Report', redate: 'Re-date Dispute Letters', second_round: 'Round 2 Letters', third_round: 'Final Demand (Round 3)' };

const Check = ({ on, onChange, label, sub, testid }) => (
  <label className="flex cursor-pointer items-start gap-2 rounded-md border border-zinc-700 bg-zinc-800/60 px-2 py-1.5 text-xs" data-testid={testid}>
    <input type="checkbox" checked={on} onChange={onChange} className="mt-0.5 accent-emerald-500" />
    <span className="min-w-0"><span className="block truncate font-semibold text-white">{label}</span>{sub && <span className="text-[10px] text-zinc-400">{sub}</span>}</span>
  </label>
);

// Pick which accounts/letters/bureaus go on the new documents (everything ticked to start).
const RedoModal = (props) => createPortal(<RedoBody {...props} />, document.body);

// Opens right where you tapped: below the tap if it's in the top half of the screen, above it otherwise.
const anchorStyle = (at) => {
  const vh = window.innerHeight;
  const y = at ?? vh / 2;
  if (y < vh / 2) { const top = Math.max(12, Math.min(y - 20, vh - 360)); return { top, maxHeight: vh - top - 12 }; }
  const bottom = Math.max(12, Math.min(vh - y - 20, vh - 360));
  return { bottom, maxHeight: vh - bottom - 12 };
};

const RedoBody = ({ mode, items, bureausInit, onClose, onRun, busy, at }) => {
  const [keep, setKeep] = useState(() => new Set(items.map((i) => i.k)));
  const [bureaus, setBureaus] = useState(() => new Set(bureausInit));
  const [sworn, setSworn] = useState(true);
  const flip = (set, setter, v) => { const n = new Set(set); if (n.has(v)) n.delete(v); else n.add(v); setter(n); };
  const round = mode === 'second_round' || mode === 'third_round';
  const ready = keep.size > 0 && (!round || bureaus.size > 0);
  return (
    <div className="fixed inset-0 z-[9999] bg-black/60" data-testid="quick-redo-modal">
      <div style={anchorStyle(at)} className="absolute left-1/2 w-[calc(100%-24px)] max-w-md -translate-x-1/2 overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-900 p-4 shadow-2xl" data-testid="quick-redo-box">
        <div className="mb-2 flex items-center justify-between">
          <p className="font-bold text-white" data-testid="quick-redo-title">{TITLES[mode]}</p>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="quick-redo-close"><X className="h-4 w-4 text-zinc-400" /></button>
        </div>
        <p className="mb-2 text-[11px] text-zinc-400">
          Dated today ({todayIso()}){mode === 'ftc' ? ' with a new report number' : ''}. Untick anything already removed. The old {mode === 'ftc' ? 'report is' : 'letters are'} replaced.
        </p>
        <div className="mb-1 flex justify-between text-[11px] text-zinc-400">
          <span>{mode === 'redate' ? 'Letters' : 'Accounts'} · {keep.size} of {items.length}</span>
          <button type="button" className="underline" onClick={() => setKeep(keep.size === items.length ? new Set() : new Set(items.map((i) => i.k)))} data-testid="quick-redo-toggle-all">
            {keep.size === items.length ? 'Untick all' : 'Tick all'}
          </button>
        </div>
        <div className="space-y-1">
          {items.map((i) => <Check key={i.k} on={keep.has(i.k)} onChange={() => flip(keep, setKeep, i.k)} label={i.label} sub={i.sub} testid={`quick-redo-item-${i.k.replace(/[^a-z0-9]/g, '-').slice(0, 40)}`} />)}
        </div>
        {round && (
          <div className="mt-3">
            <p className="mb-1 text-[11px] font-bold text-zinc-300">Bureaus (all on ONE letter, addresses stacked at the top)</p>
            <div className="flex gap-1.5">
              {BUREAUS.map(([b, l]) => (
                <button key={b} type="button" onClick={() => flip(bureaus, setBureaus, b)} data-testid={`quick-redo-bureau-${b}`}
                  className={`flex-1 rounded-md border px-2 py-1 text-xs font-bold ${bureaus.has(b) ? 'border-emerald-400 bg-emerald-500/20 text-emerald-100' : 'border-zinc-700 text-zinc-400'}`}>{l}</button>
              ))}
            </div>
            <div className="mt-2"><Check on={sworn} onChange={() => setSworn(!sworn)} label="Also download the sworn statement" testid="quick-redo-sworn" /></div>
          </div>
        )}
        <button type="button" disabled={!ready || busy} onClick={() => onRun(keep, bureaus, sworn)} data-testid="quick-redo-generate"
          className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-md bg-emerald-600 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" />{busy}</> : round ? `Generate 1 letter (${bureaus.size} bureau${bureaus.size === 1 ? '' : 's'})${sworn ? ' + sworn statement' : ''}` : 'Generate'}
        </button>
      </div>
    </div>
  );
};

const latestByBureau = (letters, types) => {
  const out = new Map();
  letters.filter((l) => types.includes(l.letter_type || 'regular')).forEach((l) => { const b = String(l.bureau).toLowerCase(); if (!out.has(b)) out.set(b, l); });
  return [...out.values()];
};

const bureausOf = (src) => {
  const s = new Set(src.flatMap((l) => (String(l.bureau).toLowerCase() === 'all' ? BUREAUS.map(([b]) => b) : [String(l.bureau).toLowerCase()])));
  const picked = BUREAUS.map(([b]) => b).filter((b) => s.has(b));
  return picked.length ? picked : BUREAUS.map(([b]) => b);
};

// "Quick redo" for a client with a previous report: new FTC report, re-dated letters, Round 2, Final Demand.
export default function QuickRedoPanel({ report, letters, onChanged }) {
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState('');
  const open = async (mode, at) => {
    try {
      if (mode === 'ftc') {
        const { data } = await axios.get(`${API}/api/ftc-reports/${report.id}`);
        const items = ftcAccounts(data).map((a) => ({ k: accountKey(a), label: acctName(a), sub: `Section ${a._section}${a.balance ? ` · $${a.balance}` : ''}` }));
        if (!items.length) { toast.error('This FTC report has no accounts saved'); return; }
        setModal({ mode, at, items, full: data });
      } else if (mode === 'redate') {
        if (!letters.length) { toast.error('No saved dispute letters for this client yet'); return; }
        setModal({ mode, at, items: letters.map((l) => ({ k: l.id, label: `${(l.letter_type || 'regular').replace('_', ' ')} · ${String(l.bureau || '').toUpperCase()}`, sub: `${l.accounts?.length || 0} accounts · was ${l.letter_date || l.updated_at?.slice(0, 10)}` })) });
      } else {
        let src = latestByBureau(letters, ROUND[mode].slice(0, 1));
        if (!src.length) src = latestByBureau(letters, ROUND[mode]);
        let accts = uniqueAccounts(src.map((l) => l.accounts || []));
        if (!accts.length) accts = ftcAccounts((await axios.get(`${API}/api/ftc-reports/${report.id}`)).data);
        if (!accts.length) { toast.error('No accounts found on earlier letters or the FTC report'); return; }
        const base = src[0] || letters[0] || { client_name: report.client_name };
        setModal({ mode, at, base, accts, bureaus: bureausOf(src), items: accts.map((a) => ({ k: accountKey(a), label: acctName(a), sub: [a.account_number && `#${a.account_number}`, a.category].filter(Boolean).join(' · ') })) });
      }
    } catch (e) { toast.error(await errText(e)); }
  };
  const run = async (keep, bureaus, sworn) => {
    const { mode } = modal;
    try {
      if (mode === 'ftc') {
        setBusy('Building FTC report…');
        const n = await regenerateFtc(modal.full, keep);
        toast.success(`New FTC report${n ? ` #${n}` : ''} downloaded`);
      } else if (mode === 'redate') {
        const pick = letters.filter((l) => keep.has(l.id));
        for (let i = 0; i < pick.length; i += 1) { setBusy(`Letter ${i + 1} of ${pick.length}…`); await redateLetter(pick[i]); }
        toast.success(`${pick.length} letter${pick.length === 1 ? '' : 's'} re-dated to today`);
      } else {
        const accounts = modal.accts.filter((a) => keep.has(accountKey(a)));
        const bs = BUREAUS.map(([b]) => b).filter((b) => bureaus.has(b));
        setBusy(sworn ? 'Building letter + sworn statement…' : 'Building letter…');
        await makeRoundLetter({ base: modal.base, letterType: mode, bureaus: bs, accounts, existing: letters, sworn });
        toast.success(`${TITLES[mode]}: 1 letter to ${bs.length} bureau${bs.length === 1 ? '' : 's'} with ${accounts.length} accounts${sworn ? ' + sworn statement' : ''}`);
      }
      setModal(null);
      onChanged?.();
    } catch (e) { toast.error(`Failed: ${await errText(e)}`); }
    setBusy('');
  };
  const btn = 'flex items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-[11px] font-bold';
  return (
    <div className="mt-3 space-y-1.5 border-t border-zinc-800 pt-3" data-testid={`quick-redo-${report.id}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-300">Quick redo · dated today</p>
      <div className="grid grid-cols-2 gap-1.5">
        <button type="button" onClick={(e) => open('ftc', e.clientY)} className={`${btn} border-blue-400/40 bg-blue-500/15 text-blue-100`} data-testid={`quick-redo-ftc-${report.id}`}><RefreshCw className="h-3.5 w-3.5" />New FTC Report</button>
        <button type="button" onClick={(e) => open('redate', e.clientY)} className={`${btn} border-zinc-500/40 bg-zinc-700/40 text-zinc-100`} data-testid={`quick-redo-redate-${report.id}`}><CalendarClock className="h-3.5 w-3.5" />Re-date Letters</button>
        <button type="button" onClick={(e) => open('second_round', e.clientY)} className={`${btn} border-amber-400/40 bg-amber-500/15 text-amber-100`} data-testid={`quick-redo-round2-${report.id}`}><FileWarning className="h-3.5 w-3.5" />Round 2 Letters</button>
        <button type="button" onClick={(e) => open('third_round', e.clientY)} className={`${btn} border-red-400/40 bg-red-500/15 text-red-100`} data-testid={`quick-redo-final-${report.id}`}><Gavel className="h-3.5 w-3.5" />Final Demand</button>
      </div>
      {modal && <RedoModal at={modal.at} mode={modal.mode} items={modal.items} bureausInit={modal.bureaus || []} busy={busy} onClose={() => !busy && setModal(null)} onRun={run} />}
    </div>
  );
}
